import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPermission } from '../../../enum/userPermissions';
import usePermission from '../../../hooks/usePermission';
import { VerticalTabs } from '../../Tabs/VerticalTabs/VerticalTabs.component';
import { TabItem } from '../../Tabs/VerticalTabs/VerticalTabs.types';
import { ActivityTime } from '../ActivityTime/ActivityTime.component';
import AnnotationDefinitionsManager from '../AnnotationDefinitionsManager/AnnotationDefinitionsManager.component';
import { ConfigurationApi } from '../ConfigurationApi/ConfigurationApi.component';
import { ConfigurationKsdo } from '../ConfigurationKsdo/ConfigurationKsdo.component';
import { ConfigurationOke } from '../ConfigurationOke/ConfigurationOke.component';
import { ContextualHelpList } from '../ContextualHelp/ContextualHelpList.component';
import { SmsNotificationSetupManager } from '../SmsNotificationSetupManager/SmsNotificationSetupManager.component';
import { PasswordPolicyManager } from '../PasswordPolicyManager/PasswordPolicyManager.component';
import { SpecialCases } from '../SpecialCases/SpecialCases.component';
import { FlagSetting } from '../SystemFlags/FlagSetting.component';
import { LabelManager } from '../LabelManager/LabelManager.component';
interface SystemSettingsDialogContentProps {
  onClose: () => void;
  onCancel: () => void;
  onChange: () => void;
  activeTab: string;
  onSave: ()=> void;
}

export const SystemSettingsDialogContent: React.FC<SystemSettingsDialogContentProps> = ({
  onClose,
  onChange,
  activeTab,
  onSave,
}) => {
  const items: TabItem[] = [];
  const { t } = useTranslation();
  const hasAdminSettingsManagePermission = usePermission(UserPermission.AdminSettingsManage);
  const hasAdminAnnotationManagePermission = usePermission(UserPermission.AdminAnnotationManage);
  const hasAdminSpecialcaseManagePermission = usePermission(UserPermission.AdminSpecialcaseManage);
  const hasAdminSupportManagePermission = usePermission(UserPermission.AdminSupportManage);
  const hasAdminLabelManagePermission = usePermission(UserPermission.AdminLabelManage);

  const apiLabels = {
    api: t('systemSettings:systemModalTitles.API'),
    configuration: {
      api: t('systemSettings:systemModalTitles.configurationApi'),
      oke: `Konfiguracja ${t('global:shortNames.OKE')}`,
      ksdo: `Konfiguracja ${t('global:shortNames.KSDO')}`,
    },
  };

  if (hasAdminSettingsManagePermission) {
    items.push({
      label: t('systemSettings:systemModalTitles.flagSystem'),
      children: <FlagSetting onClose={onClose} onChange={onChange} onSave={onSave}/>,
    });

    items.push({
      label: t('systemSettings:systemModalTitles.passwordPolicyManager'),
      children: <PasswordPolicyManager onClose={onClose} onChange={onChange} />,
    });
  }


  if (hasAdminAnnotationManagePermission) {
    items.push({
      label: t('systemSettings:systemModalTitles.annotationDefinitionManager'),
      children: <AnnotationDefinitionsManager onClose={onClose} onChange={onChange} />,
    });
  }

  if (hasAdminSpecialcaseManagePermission) {
    items.push({
      label: t('systemSettings:systemModalTitles.specialCases'),
      children: <SpecialCases onClose={onClose} onChange={onChange} />,
    });
  }

  if (hasAdminSettingsManagePermission) {
    items.push({
      label: t('systemSettings:systemModalTitles.smsNotificationSetupManager'),
      children: <SmsNotificationSetupManager onClose={onClose} onSave={onSave} />,
    });
    items.push({
      label: t('systemSettings:activeTime.header'),
      children: <ActivityTime onClose={onClose} onChange={onChange} />,
    });
  }
  if (hasAdminSupportManagePermission) {
    items.push({
      label: t('systemSettings:systemModalTitles.contextualHelp'),
      children: <ContextualHelpList onClose={onClose} onChange={onChange} />,
    });
  }
  if (hasAdminLabelManagePermission) { 
    items.push({
      label: t('systemSettings:systemModalTitles.labelManager'),
      children: <LabelManager onClose={onClose} onChange={onChange} />,
    });
  }

  if (hasAdminSettingsManagePermission) {
    items.push({
      label: apiLabels.api,
      collapsable: true,
      collapsedListItems: [apiLabels.configuration.api, apiLabels.configuration.oke, apiLabels.configuration.ksdo],
    });
    items.push({
      label: apiLabels.configuration.api,
      children: <ConfigurationApi onClose={onClose} onChange={onChange} />,
      parentLabel: apiLabels.api,
    });
    items.push({
      label: apiLabels.configuration.oke,
      children: <ConfigurationOke onClose={onClose} onChange={onChange} />,
      parentLabel: apiLabels.api,
    });
    items.push({
      label: apiLabels.configuration.ksdo,
      children: <ConfigurationKsdo onClose={onClose} onChange={onChange} />,
      parentLabel: apiLabels.api,
    });
  }

  const getActiveTab = (isCollapsed?: boolean) => {
    const activeTabItem = items.find((item) => item.label === activeTab);
    if (!isCollapsed) {
      if (activeTabItem) {
        return activeTabItem.collapsable
          ? items.map((e) => e.label).indexOf(activeTab) + 1
          : items.map((e) => e.label).indexOf(activeTab);
      }
      return 0;
    } else {
      return activeTabItem && activeTabItem.collapsable ? 0 : -1;
    }
  };

  return <VerticalTabs items={items} selectedTab={getActiveTab()} />;
};
